import { z } from 'zod';

export const deliverableSchema = z.object({
  influencer_name: z.string().min(1, 'Influencer name is required'),
  platform: z.string().min(1, 'Platform is required'),
  status: z.string().min(1, 'Status is required'),
  expected_deliverable: z.string().min(1, 'Deliverable type is required'),
  deadline: z.date().optional(),
  content_url: z.string().optional(),
  notes: z.string().optional(),
});

export type DeliverableFormData = z.infer<typeof deliverableSchema>;

/**
 * Normalizes Instagram post and reel URLs:
 * - Validates host is instagram.com
 * - Normalizes /reels/ to /reel/
 * - Strips all tracking query parameters (?igsh=..., utm_...) and hashes
 * - Standardizes trailing slashes
 * Returns null if not a valid post or reel URL.
 */
export const normalizeInstagramPostUrl = (rawUrl: string): string | null => {
  try {
    const trimmed = rawUrl.trim();
    if (!trimmed) return null;

    const urlStr = trimmed.startsWith('http://') || trimmed.startsWith('https://')
      ? trimmed
      : `https://${trimmed}`;

    const parsed = new URL(urlStr);
    const host = parsed.hostname.toLowerCase();
    if (host !== 'instagram.com' && !host.endsWith('.instagram.com')) {
      return null;
    }

    const pathParts = parsed.pathname.split('/').filter(Boolean);
    const typeIdx = pathParts.findIndex((p) => p === 'reel' || p === 'p' || p === 'reels');
    if (typeIdx !== -1 && pathParts[typeIdx + 1]) {
      const type = pathParts[typeIdx] === 'reels' ? 'reel' : pathParts[typeIdx];
      const shortcode = pathParts[typeIdx + 1];
      return `https://www.instagram.com/${type}/${shortcode}/`;
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Extracts the unique shortcode from an Instagram post or reel URL.
 * Every Instagram reel, video, or post has a globally unique shortcode (e.g. "DeEF6iPNCF3").
 * URLs formatted with /p/<shortcode>/ and /reel/<shortcode>/ point to the exact same media item.
 */
export const extractInstagramShortcode = (rawUrl: string): string | null => {
  try {
    const trimmed = rawUrl.trim();
    if (!trimmed) return null;

    const urlStr = trimmed.startsWith('http://') || trimmed.startsWith('https://')
      ? trimmed
      : `https://${trimmed}`;

    const parsed = new URL(urlStr);
    const host = parsed.hostname.toLowerCase();
    if (host !== 'instagram.com' && !host.endsWith('.instagram.com')) {
      return null;
    }

    const pathParts = parsed.pathname.split('/').filter(Boolean);
    const typeIdx = pathParts.findIndex((p) => p === 'reel' || p === 'p' || p === 'reels');
    if (typeIdx !== -1 && pathParts[typeIdx + 1]) {
      return pathParts[typeIdx + 1];
    }

    return null;
  } catch {
    return null;
  }
};

